import ApiService from "../../services/apiService";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { Instrument } from "./instrument_type";
import { Label } from "reactstrap";
import DateTimePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { format } from "date-fns";

import DownloadExcel from "../downloadExcel/DownloadExcel";
import { usePagination } from "../../hooks/usePagination";
import LeftIcon from "../svg/LeftButton";
import RightIcon from "../svg/RightIcon";
import { toast } from "react-toastify";
import ValveControllerTable from "./ValveControllerCard";
import SensorTable from "./SensorCard";
import PumpControllerTable from "./PumpControllerCard";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../ui/table";
import Breadcrumbs from "../../coreComponents/breadcrumbs";

type user = {
  id: number;
  fullName: string;
  email: string;
  mobileNumber: string;
  houseNo: string;
  streetLandmark: string;
  area: string;
  village: string;
  city: string;
  district: string;
  state: string;
  pincode: string;
  country: string;
  mailStatus: string;
  role: string;
  enabled: number;
  deleteDateTime: null | string;
  createdDateTime: null | string;
};

type InstrumentHistoryItem = {
  dateTime: string;
  packetType: string;
  classType: string;

  on_Join: number;
  live_History: number;
  fixedInterval: number;
  onRequest: number;
  operateBit: number;
  moistureBit: number;
  alarmBit: number;
  s_ClassChange: number;
  midnightReset: number;

  beacon_Event: number;
  beaconStatus: number;

  firmwareVersion: number;
  batteryVoltage: number;
  battery_Percent: number;
  temperature: number;

  status: number;
  highTemperatureAlarm: number;
  lowBatteryAlarm: number;
  readSwitchHigh: number;

  dI2: number | null;
  doorStatus: number;
  dI1Counter: number;
  dI2Counter: number;

  rssi: number;
  snr: number;
  spreadingFactor: number;

  p_SysHealthy: number;
  p_CurrentFault: number;
  p_PhaseFault: number;
  p_Phase1: number;
  p_Phase2: number;
  p_Phase3: number;
  p_PowerStatus: number;
  p_OverCurrentBit: number;
  p_UnderCurrentBit: number;

  ctValue: number | null;
  ackBit: number | null;
  p_LocalRemote: string | null;

  dry: number | null;
  wet: number | null;
  dry_SetPoint: number | null;
  wet_SetPoint: number | null;
  moisturePercentage: number | null;
};

const UserInfoAndActivity = () => {
  const location = useLocation();
  const [userDetail, setUserDetail] = useState<user>();
  const [userPhone, setUserPhone] = useState<string>();
  const [instruments, setInstruments] = useState<Instrument[]>([]);
  const [selectedInstrumentId, setSelectedInstrumentId] = useState<number | null>(null);
  const [fromDateTime, setFromDateTime] = useState<Date | null>(null);
  const [toDateTime, setToDateTime] = useState<Date | null>(null);
  const [deviceType, setDeviceType] = useState<string>("");
  const [isEnabled, setIsEnabled] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);
  const [instrumentName, setInstrumentName] = useState<string>();
  const [isStatusCollapsed, setIsStatusCollapsed] = useState(false);
  const detailSectionRef = useRef<HTMLDivElement | null>(null);
  let updatedData: any = [];

  useEffect(() => {
    const now = new Date();
    const startOfDay = new Date();
    startOfDay.setHours(0, 0, 0, 0);
    setFromDateTime(startOfDay);
    setToDateTime(now);
  }, []);

  const getUserDetail = async (phoneNumber?: string) => {
    const mobile = phoneNumber ?? userPhone;
    if (!mobile) {
      toast.error("Enter user mobile number");
      return;
    }
    try {
      const response = await ApiService.axiosApi(`/users/get-by-mobile/${mobile}`, {
        method: "GET",
      });
      if (response.success) {
        setUserDetail(response.data);
        getUserInstruments(response.data.id);
      } else {
        console.error(response.message, "unable to get user detail");
      }
    } catch (e) {
      console.error(e);
    }
  };

  useEffect(() => {
    const state = location.state as { mobileNumber?: string } | null;
    const mobileNumber = state?.mobileNumber;
    if (mobileNumber) {
      setUserPhone(mobileNumber);
      void getUserDetail(mobileNumber);
    }
  }, [location.state]);

  const {
    data,
    pageNumber,
    totalPages,
    canGoToNextPage,
    canGoToPreviousPage,
    goToNextPage,
    goToPreviousPage,
    resetPagination,
    setFromDate,
    setToDate,
    setId,
  } = usePagination({
    endpoint: `/InstrumentHistory/getbyiddatetime`,
    refreshKey,
    enabled: isEnabled,
  });

  const handleClick = () => {
    resetPagination();
    setFromDate(fromDateTime);
    setToDate(toDateTime);
    setId(selectedInstrumentId as number);
    setIsEnabled(true);
    setRefreshKey((prev) => prev + 1);
  };

  const openInstrumentDetails = useCallback(
    (device: Instrument) => {
      setSelectedInstrumentId(device.id);
      setDeviceType(device.deviceType);
      setInstrumentName(device.instrumentName);
      resetPagination();
      setFromDate(fromDateTime);
      setToDate(toDateTime);
      setId(device.id);
      setIsEnabled(true);
      setRefreshKey((prev) => prev + 1);

      setTimeout(() => {
        detailSectionRef.current?.scrollIntoView({
          behavior: "smooth",
          block: "start",
        });
      }, 0);
    },
    [fromDateTime, toDateTime, resetPagination, setFromDate, setToDate, setId],
  );

  updatedData = useMemo(() => {
    if (!data || !Array.isArray(data)) return [];
    return data.map((time) => ({
      ...time,
      dateTime: time.dateTime
        ? format(new Date(time.dateTime), "dd-MM-yyyy HH:mm:ss")
        : "-",
    }));
  }, [data, refreshKey]);

  const getUserInstruments = async (userId: number) => {
    try {
      const response = await ApiService.axiosApi(
        `/instrumentmaster/user/${userId}`,
        { method: "GET" },
      );
      if (response.success) {
        const order: Record<string, number> = {
          "Pump Controller": 1,
          "Pump Comtroller": 1,
          "Valve Controller": 2,
          Sensor: 3,
        };
        const sorted = (response.data.instruments ?? []).sort(
          (a: Instrument, b: Instrument) =>
            (order[a.deviceType] ?? 99) - (order[b.deviceType] ?? 99),
        );
        setInstruments(sorted);
      }
    } catch (e) {
      console.error(e);
    }
  };

  const getColumns = () => {
    const commonColumns = [
      { header: "Date Time", accessor: "dateTime" },
      { header: "Packet", accessor: "packetType" },
      { header: "Join", accessor: "on_Join" },
      { header: "Live Data", accessor: "live_History" },
      { header: "Fixed Interval", accessor: "fixedInterval" },
      { header: "On Request", accessor: "onRequest" },
      { header: "Alarm", accessor: "alarmBit" },
      { header: "Midnight", accessor: "midnightReset" },
      { header: "High Temperature", accessor: "highTemperatureAlarm" },
      { header: "Low Battery", accessor: "lowBatteryAlarm" },
      { header: "Door", accessor: "doorStatus" },
      { header: "Temperature", accessor: "temperature" },
      { header: "Firmware Version", accessor: "firmwareVersion" },
      { header: "Rssi", accessor: "rssi" },
      { header: "Snr", accessor: "snr" },
      { header: "Spreading Factor", accessor: "spreadingFactor" },
    ];

    if (deviceType === "Pump Controller" || deviceType === "Pump Comtroller") {
      return [
        ...commonColumns,
        { header: "Battery Voltage", accessor: "batteryVoltage" },
        { header: "Status", accessor: "status" },
        { header: "Operate", accessor: "operateBit" },
        { header: "Acknowledge", accessor: "ackBit" },
        { header: "Watermeter", accessor: "dI1Counter" },
        { header: "Sys. Health", accessor: "p_SysHealthy" },
        { header: "Current Fault", accessor: "p_CurrentFault" },
        { header: "Phase Fault", accessor: "p_PhaseFault" },
        { header: "Phase 1", accessor: "p_Phase1" },
        { header: "Phase 2", accessor: "p_Phase2" },
        { header: "Phase 3", accessor: "p_Phase3" },
        { header: "Power Status", accessor: "p_PowerStatus" },
        { header: "Over Current", accessor: "p_OverCurrentBit" },
        { header: "Under Current", accessor: "p_UnderCurrentBit" },
        { header: "Current Ampere", accessor: "ctValue" },
        { header: "Local Remote", accessor: "p_LocalRemote" },
      ];
    }

    if (deviceType === "Valve Controller") {
      return [
        ...commonColumns,
        { header: "Status", accessor: "status" },
        { header: "Operate", accessor: "operateBit" },
        { header: "Beacon Event", accessor: "beacon_Event" },
        { header: "Beacon Status", accessor: "beaconStatus" },
        { header: "Battery Voltage", accessor: "batteryVoltage" },
        { header: "Battery Percent", accessor: "battery_Percent" },
        { header: "Readswitch", accessor: "readSwitchHigh" },
        { header: "Watermeter1", accessor: "dI1Counter" },
        { header: "Watermeter2", accessor: "dI2Counter" },
      ];
    }

    if (deviceType === "Sensor") {
      return [
        ...commonColumns,
        { header: "Class", accessor: "classType" },
        { header: "Class Change", accessor: "s_ClassChange" },
        { header: "Moisture Bit", accessor: "moistureBit" },
        { header: "Moisture Percentage", accessor: "moisturePercentage" },
        { header: "Battery Voltage", accessor: "batteryVoltage" },
        { header: "Battery Percent", accessor: "battery_Percent" },
        { header: "Dry", accessor: "dry" },
        { header: "Wet", accessor: "wet" },
        { header: "Dry Setpoint", accessor: "dry_SetPoint" },
        { header: "Wet Setpoint", accessor: "wet_SetPoint" },
        { header: "Readswitch", accessor: "readSwitchHigh" },
        { header: "Beacon Status", accessor: "beaconStatus" },
      ];
    }

    return commonColumns;
  };

  const instrumentSections = useMemo(
    () => [
      {
        key: "Pump Controller",
        title: "Pump Controllers",
        items: instruments.filter(
          (device) =>
            device.deviceType === "Pump Controller" ||
            device.deviceType === "Pump Comtroller",
        ),
      },
      {
        key: "Valve Controller",
        title: "Valve Controllers",
        items: instruments.filter(
          (device) => device.deviceType === "Valve Controller",
        ),
      },
      {
        key: "Sensor",
        title: "Sensors",
        items: instruments.filter((device) => device.deviceType === "Sensor"),
      },
    ],
    [instruments],
  );

  const fetchAllDataForExport = async () => {
    try {
      const response = await ApiService.axiosApi(
        `/InstrumentHistory/getbyiddatetime`, 
        {
          method: "GET",
          params: {
            pageNumber: 0,
            pageSize: 0,
            instrumentId: selectedInstrumentId,
            fromDateTime: fromDateTime
              ? format(fromDateTime, "yyyy-MM-dd HH:mm:ss")
              : "",
            toDateTime: toDateTime
              ? format(toDateTime, "yyyy-MM-dd HH:mm:ss")
              : "",
          },
        },
      );
      if (response.success) {
        return response.data.data;
      } else {
        toast.error("Unable to fetch full report");
        return [];
      }
    } catch (error) {
      console.log(error);
      toast.error("API Error");
      return [];
    }
  };

  const getFilteredDataForExcel = async () => {
    const columns = getColumns();
    const allData = await fetchAllDataForExport();
    return allData.map((row: any) => {
      const filteredRow: Record<string, any> = {};
      columns.forEach(({ header, accessor }) => {
        const key = accessor?.trim();
        let value = row[key];
        if (key === "dateTime" && value) {
          value = format(new Date(value), "dd-MM-yyyy HH:mm:ss");
        }
        filteredRow[header] = value ?? "-";
      });
      return filteredRow;
    });
  };

  return (
    <>
      <Breadcrumbs mainTitle="User Lookup" parent="Users" />
      <div className="userlookup">
        <div className="user-search-section">
          <div className="user-search-box">
            <input
              value={userPhone}
              onChange={(e) => setUserPhone(e.target.value)}
              type="text"
              placeholder="Enter User Mobile Number"
            />
            <button onClick={() => void getUserDetail()}>Search</button>
          </div>
        </div>

        <div className="user-main-content">
          <div className="user-card">
            {/* Avatar header */}
            <div className="uc-header">
              <div className="uc-avatar">
                {userDetail?.fullName
                  ? userDetail.fullName
                      .split(" ")
                      .map((n) => n[0])
                      .join("")
                      .slice(0, 2)
                      .toUpperCase()
                  : "?"}
              </div>
              <div className="uc-header-info">
                <p className="uc-name">{userDetail?.fullName || "-"}</p>
                <p className="uc-mobile">{userDetail?.mobileNumber || "-"}</p>
              </div>
              <span
                className={`status-badge ${userDetail?.enabled == 1 ? "active" : "inactive"}`}
              >
                {userDetail?.enabled == 1 ? "Active" : "Inactive"}
              </span>
            </div>

            <div className="vc-divider" />

            {/* Compact rows */}
            <div className="uc-rows">
              {[
                { label: "Email", value: userDetail?.email },
                { label: "Country", value: userDetail?.country },
                { label: "State", value: userDetail?.state },
                { label: "City", value: userDetail?.city },
                { label: "District", value: userDetail?.district },
                {
                  label: "Created At",
                  value: userDetail?.createdDateTime
                    ? format(
                        new Date(userDetail.createdDateTime),
                        "dd MMM yyyy, HH:mm",
                      )
                    : "-",
                },
              ].map(({ label, value }) => (
                <div key={label} className="uc-row">
                  <span className="uc-label">{label}</span>
                  <span className="uc-value">{value || "-"}</span>
                </div>
              ))}
            </div>
          </div>

          <div className="user-activity-card">
            <div
              className="uc-collapse-header"
              onClick={() => setIsStatusCollapsed((prev) => !prev)}
            >
              <h2 style={{ margin: 0 }}>Current Status</h2>
              <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                <span style={{ fontSize: "12px", color: "#6B7280" }}>
                  {instruments.length} device
                  {instruments.length !== 1 ? "s" : ""}
                </span>
                <svg
                  width="18"
                  height="18"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="#6B7280"
                  strokeWidth="2"
                  style={{
                    transform: isStatusCollapsed ? "rotate(-90deg)" : "rotate(0deg)",
                    transition: "transform 0.2s ease",
                  }}
                >
                  <polyline points="6 9 12 15 18 9" />
                </svg>
              </div>
            </div>

            {!isStatusCollapsed && (
              <div className="instrument-list">
                {instrumentSections.map(
                  (section) =>
                    section.items.length > 0 && (
                      <div key={section.key} className="instrument-section">
                        <div className="instrument-section-header">
                          <h3 className="instrument-section-title">
                            {section.title}
                          </h3>
                          <span className="instrument-section-count">
                            {section.items.length}
                          </span>
                        </div>

                        <div className="instrument-section-list">
                          {section.key === "Pump Controller" && (
                            <PumpControllerTable
                              data={section.items}
                              onSelect={openInstrumentDetails}
                              selectedId={selectedInstrumentId}
                            />
                          )}
                          {section.key === "Valve Controller" && (
                            <ValveControllerTable
                              data={section.items}
                              onSelect={openInstrumentDetails}
                              selectedId={selectedInstrumentId}
                            />
                          )}
                          {section.key === "Sensor" && (
                            <SensorTable
                              data={section.items}
                              onSelect={openInstrumentDetails}
                              selectedId={selectedInstrumentId}
                            />
                          )}
                        </div>
                      </div>
                    ),
                )}
              </div>
            )}
          </div>
        </div>

        {selectedInstrumentId && (
          <div className="datetime-bar-wrapper" ref={detailSectionRef}>
            <div className="datetime-bar justify-content-center">
              <div className="datetime-group">
                <span className="dt-label">From</span>
                <div className="dt-input-wrap">
                  <svg
                    width="14"
                    height="14"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="#4B5563"
                    strokeWidth="2"
                  >
                    <rect x="3" y="4" width="18" height="18" rx="2" />
                    <line x1="16" y1="2" x2="16" y2="6" />
                    <line x1="8" y1="2" x2="8" y2="6" />
                    <line x1="3" y1="10" x2="21" y2="10" />
                  </svg>
                  <DateTimePicker
                    className="report-input"
                    dateFormat="dd/MM/yyyy HH:mm:ss"
                    showTimeSelect
                    selected={fromDateTime}
                    onChange={(date: Date | null) => setFromDateTime(date)}
                    timeFormat="HH:mm:ss"
                  />
                </div>
              </div>

              <div className="dt-arrow">
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="#9CA3AF"
                  strokeWidth="2"
                >
                  <line x1="5" y1="12" x2="19" y2="12" />
                  <polyline points="12 5 19 12 12 19" />
                </svg>
              </div>

              <div className="datetime-group">
                <span className="dt-label">To</span>
                <div className="dt-input-wrap">
                  <svg
                    width="14"
                    height="14"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="#4B5563"
                    strokeWidth="2"
                  >
                    <rect x="3" y="4" width="18" height="18" rx="2" />
                    <line x1="16" y1="2" x2="16" y2="6" />
                    <line x1="8" y1="2" x2="8" y2="6" />
                    <line x1="3" y1="10" x2="21" y2="10" />
                  </svg>
                  <DateTimePicker
                    className="report-input"
                    dateFormat="dd/MM/yyyy HH:mm:ss"
                    showTimeSelect
                    selected={toDateTime}
                    onChange={(date: Date | null) => setToDateTime(date)}
                    timeFormat="HH:mm:ss"
                    minDate={fromDateTime || undefined}
                  />
                </div>
              </div>

              <div className="dt-divider" />
              <div className="datetime-group">
                <Label className="dt-label" style={{ visibility: "hidden" }} />
                <button className="filter-btn dt-btn" onClick={handleClick}>
                  <svg
                    width="14"
                    height="14"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="white"
                    strokeWidth="2"
                  >
                    <polyline points="22 12 18 12 15 21 9 3 6 12 2 12" />
                  </svg>
                  Generate
                </button>
              </div>

              <div className="d-flex flex-column">
                <Label style={{ visibility: "hidden" }}>Generate</Label>
                <DownloadExcel
                  InstrumentName={instrumentName ? instrumentName : "ValvePumpHistory"}
                  getData={getFilteredDataForExcel}
                  ExcelSheetName={`${instrumentName}_History`}
                />
              </div>
            </div>
          </div>
        )}

        {selectedInstrumentId && data?.length > 0 && (
          <div style={{ width: "100%", marginTop: "16px" }}>
          <Table
            wrapperClassName="table-responsive rounded border"
            className="table table-hover table-bordered align-middle text-center mb-0"
          >
            <TableHeader className="bg-light-primary">
              <TableRow>
                {getColumns().map((column, index) => (
                  <TableHead
                    key={index}
                    className="text-center text-nowrap"
                  >
                    {column.header}
                  </TableHead>
                ))}
              </TableRow>
            </TableHeader>
        
            <TableBody>
              {updatedData.map((row: any, rowIndex: number) => (
                <TableRow key={rowIndex}>
                  {getColumns().map((column, colIndex) => (
                    <TableCell key={colIndex}>
                      {row[column.accessor] ?? "-"}
                    </TableCell>
                  ))}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
        )}

        {selectedInstrumentId && data?.length > 0 && (
          <div
            className="d-flex justify-content-end align-content-center"
            style={{ marginRight: "10px", marginTop: "10px" }}
          >
            <p style={{ marginRight: "10px", marginTop: "8px" }}>
              Page {pageNumber} of {totalPages}
            </p>
            <button
              onClick={goToPreviousPage}
              disabled={!canGoToPreviousPage}
              className="icon-button"
            >
              <LeftIcon className="icon" />
            </button>
            <button
              className={`icon-button ${!canGoToNextPage ? "disabled" : ""}`}
              onClick={goToNextPage}
              disabled={!canGoToNextPage}
            >
              <RightIcon className="icon" />
            </button>
          </div>
        )}
      </div>
    </>
  );
};

export default UserInfoAndActivity;
